import React, { useState, useEffect, useContext } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Table,
  Badge,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Spinner,
  FormGroup,
  Label,
  Input,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";

import EmpresaContext from "../../../contexts/Empresa";
import AuthContext from "../../../contexts/Auth";
import api from "../../../services/api";

const ListagemRotas = ({ show, onClose, notify, onVerDetalhes }) => {
  const { empresaSelecionada } = useContext(EmpresaContext);
  const { auth } = useContext(AuthContext);

  // Estados principais
  const [rotas, setRotas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [colaboradores, setColaboradores] = useState([]);
  const [loadingColaboradores, setLoadingColaboradores] = useState(false);

  // Estados de filtros
  const [filtros, setFiltros] = useState({
    data_inicio: "",
    data_fim: "",
    colaborador_id: "",
  });

  // Estados de paginação
  const [paginacao, setPaginacao] = useState({
    page: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
  });

  useEffect(() => {
    if (show && empresaSelecionada?.id) {
      buscarColaboradores();
      buscarRotas();
    }
  }, [show, empresaSelecionada]);

  // Buscar colaboradores para o filtro
  const buscarColaboradores = async () => {
    setLoadingColaboradores(true);
    try {
      const response = await api.get(
        `/common/empresas/${empresaSelecionada.id}/colaboradores?situacaoNaEmpresa=A`,
        {
          headers: {
            Authorization: `Bearer ${auth.token}`,
          },
        }
      );
      setColaboradores(response.data || []);
    } catch (error) {
      console.error("Erro ao buscar colaboradores:", error);
      notify("danger", "Erro ao carregar colaboradores");
      setColaboradores([]);
    } finally {
      setLoadingColaboradores(false);
    }
  };

  // Buscar rotas com filtros e paginação
  const buscarRotas = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: paginacao.perPage.toString(),
        ...Object.fromEntries(
          Object.entries(filtros).filter(([_, value]) => value !== "")
        ),
      });

      const response = await api.get(
        `/common/empresas/${empresaSelecionada.id}/rotas-visitas?${params}`,
        {
          headers: {
            Authorization: `Bearer ${auth.token}`,
          },
        }
      );

      setRotas(response.data.data || []);
      setPaginacao(response.data.pagination || paginacao);
    } catch (error) {
      console.error("Erro ao buscar rotas:", error);
      notify("danger", "Erro ao carregar rotas de visitas");
      setRotas([]);
    } finally {
      setLoading(false);
    }
  };

  // Aplicar filtros
  const aplicarFiltros = () => {
    // Validar filtros de data
    if (filtros.data_inicio && filtros.data_fim && filtros.data_inicio > filtros.data_fim) {
      notify("warning", "A data de início deve ser anterior ou igual à data final");
      return;
    }

    buscarRotas(1);
  };

  // Limpar filtros
  const limparFiltros = () => {
    setFiltros({
      data_inicio: "",
      data_fim: "",
      colaborador_id: "",
    });
    // Buscar sem filtros
    setTimeout(() => buscarRotas(1), 100);
  };

  // Formatar data
  const formatarData = (data) => {
    return new Date(data).toLocaleDateString("pt-BR");
  };

  // Formatar tempo
  const formatarTempo = (minutos) => {
    if (!minutos) return "N/A";
    const horas = Math.floor(minutos / 60);
    const mins = minutos % 60;
    return horas > 0 ? `${horas}h ${mins}min` : `${mins}min`;
  };

  // Badge de status
  const getStatusBadge = (status) => {
    const statusConfig = {
      pendente: { color: "secondary", text: "Pendente" },
      em_andamento: { color: "warning", text: "Em Andamento" },
      concluida: { color: "success", text: "Concluída" },
    };

    const config = statusConfig[status] || { color: "secondary", text: status };
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  // Navegar páginas
  const irParaPagina = (page) => {
    if (page >= 1 && page <= paginacao.lastPage) {
      buscarRotas(page);
    }
  };

  return (
    <Modal isOpen={show} toggle={onClose} size="xl" style={{ maxWidth: "95%" }}>
      <ModalHeader toggle={onClose}>
        Gerenciar Rotas de Visitas
      </ModalHeader>
      <ModalBody>
        {/* Filtros */}
        <Card className="mb-4">
          <CardHeader>
            <h6 className="mb-0">Filtros de Busca</h6>
          </CardHeader>
          <CardBody>
            <Row>
              <Col md="3">
                <FormGroup>
                  <Label>Data Início</Label>
                  <Input
                    type="date"
                    value={filtros.data_inicio}
                    onChange={(e) =>
                      setFiltros({ ...filtros, data_inicio: e.target.value })
                    }
                  />
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label>Data Final</Label>
                  <Input
                    type="date"
                    value={filtros.data_fim}
                    onChange={(e) =>
                      setFiltros({ ...filtros, data_fim: e.target.value })
                    }
                  />
                </FormGroup>
              </Col>
              <Col md="4">
                <FormGroup>
                  <Label>Colaborador</Label>
                  <Input
                    type="select"
                    value={filtros.colaborador_id}
                    onChange={(e) =>
                      setFiltros({ ...filtros, colaborador_id: e.target.value })
                    }
                    disabled={loadingColaboradores}
                  >
                    <option value="">
                      {loadingColaboradores ? "Carregando..." : "Todos os colaboradores"}
                    </option>
                    {colaboradores.map((colab) => (
                      <option key={colab.id} value={colab.id}>
                        {colab.name}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md="2" className="d-flex align-items-end">
                <FormGroup className="w-100">
                  <Button color="primary" onClick={aplicarFiltros} block className="mb-2">
                    Filtrar
                  </Button>
                  <Button color="secondary" onClick={limparFiltros} block>
                    Limpar
                  </Button>
                </FormGroup>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Tabela de Rotas */}
        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="mt-2">Carregando rotas...</p>
          </div>
        ) : rotas.length === 0 ? (
          <div className="text-center py-4">
            <p>Nenhuma rota encontrada para os filtros aplicados.</p>
            <small className="text-muted">
              Tente ajustar os filtros ou criar uma nova rota de visitas.
            </small>
          </div>
        ) : (
          <>
            <Table responsive striped>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Data Início</th>
                  <th>Data Final</th>
                  <th>Colaborador</th>
                  <th>Status</th>
                  <th>Visitas Definidas</th>
                  <th>Visitas Realizadas</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {rotas.map((rota) => (
                  <tr key={rota.id}>
                    <td>#{rota.id}</td>
                    <td>{formatarData(rota.data_inicio)}</td>
                    <td>{formatarData(rota.data_fim)}</td>
                    <td>{rota.colaborador_nome || "N/A"}</td>
                    <td>{getStatusBadge(rota.status)}</td>
                    <td>{rota.total_visitas}</td>
                    <td>{rota.visitas_realizadas}</td>
                    <td>
                      <Button
                        size="sm"
                        color="info"
                        onClick={() => onVerDetalhes(rota.id)}
                      >
                        Ver Detalhes
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {/* Paginação */}
            {paginacao.lastPage > 1 && (
              <div className="d-flex justify-content-center mt-3">
                <Pagination>
                  <PaginationItem disabled={paginacao.page === 1}>
                    <PaginationLink onClick={() => irParaPagina(paginacao.page - 1)}>
                      Anterior
                    </PaginationLink>
                  </PaginationItem>

                  {Array.from({ length: Math.min(5, paginacao.lastPage) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <PaginationItem key={page} active={page === paginacao.page}>
                        <PaginationLink onClick={() => irParaPagina(page)}>
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}

                  <PaginationItem disabled={paginacao.page === paginacao.lastPage}>
                    <PaginationLink onClick={() => irParaPagina(paginacao.page + 1)}>
                      Próxima
                    </PaginationLink>
                  </PaginationItem>
                </Pagination>
              </div>
            )}
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default ListagemRotas;
